import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import Footer from '../components/Footer';

const CUISINES = ['Casual Dining', 'BBQ', 'International', 'Asian Fusion', 'Korean', 'Mexican', 'Japanese', 'Bakeries/Desserts', 'Juice Bar', 'Vegetarian/Vegan', 'Indigenous Cuisine', 'Farm-to-Table', 'Contemporary', 'Seafood', 'Breweries/Distilleries', 'Fine Dining'];

export default function SubmitTruck() {
  const [form, setForm] = useState({
    name: '', type: 'truck', city: '', cuisine: 'Casual Dining', price: '$',
    description: '', instagram: '', twitter: '', website: '',
    contact_email: '', contact_name: '',
  });
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErr('');
    try {
      await apiFetch('/api/submissions/truck', { method: 'POST', body: form });
      setStatus('success');
    } catch (ex) {
      setErr(ex.message || 'Submission failed.');
      setStatus('idle');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold mb-2">Submission received!</h1>
          <p className="text-gray-600 mb-6">Thanks for adding your truck to Culinera. Our team reviews submissions within a couple of days. We&rsquo;ll email <strong>{form.contact_email}</strong> when your listing is live.</p>
          <Link to="/" className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700">← Back to Culinera</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </Link>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-semibold">← Home</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Submit your truck or restaurant</h1>
        <p className="text-gray-700 mb-8">Run an Alberta food truck or restaurant? Add it to Culinera&rsquo;s directory in two minutes. We&rsquo;ll review and approve within a couple of business days — free, no catch.</p>

        <form onSubmit={submit} className="bg-white rounded-lg shadow-sm p-6 space-y-4">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">{err}</div>}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">What are you adding? *</label>
            <div className="flex gap-2">
              {['truck', 'restaurant'].map((t) => (
                <button key={t} type="button" data-testid={`sub-type-${t}`} onClick={() => setForm({ ...form, type: t })}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold text-sm border ${form.type === t ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                  {t === 'truck' ? '🚚 Food Truck' : '🍽️ Restaurant'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">{form.type === 'truck' ? 'Truck' : 'Restaurant'} name *</label>
            <input data-testid="sub-name" required value={form.name} onChange={update('name')} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" placeholder={form.type === 'truck' ? 'e.g. Naaco Truck' : 'e.g. River Café'} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">City *</label>
              <input data-testid="sub-city" required value={form.city} onChange={update('city')} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="Edmonton" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Price *</label>
              <select data-testid="sub-price" value={form.price} onChange={update('price')} className="w-full p-3 border border-gray-300 rounded-lg">
                <option>$</option><option>$$</option><option>$$$</option><option>$$$$</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Cuisine *</label>
            <select data-testid="sub-cuisine" value={form.cuisine} onChange={update('cuisine')} className="w-full p-3 border border-gray-300 rounded-lg">
              {CUISINES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Description *<span className="text-gray-400 font-normal"> (min 20 chars)</span></label>
            <textarea data-testid="sub-description" required minLength={20} maxLength={600} rows={4} value={form.description} onChange={update('description')} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="What you serve, what makes you special, key menu items…" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Instagram handle</label>
              <input data-testid="sub-instagram" value={form.instagram} onChange={update('instagram')} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="@yourtruck or yourtruck" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">X / Twitter handle</label>
              <input data-testid="sub-twitter" value={form.twitter} onChange={update('twitter')} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="@yourtruck or yourtruck" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Website (optional)</label>
            <input data-testid="sub-website" type="url" value={form.website} onChange={update('website')} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="https://" />
          </div>

          <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Your name</label>
              <input data-testid="sub-contact-name" value={form.contact_name} onChange={update('contact_name')} className="w-full p-3 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Your email *</label>
              <input data-testid="sub-contact-email" type="email" required value={form.contact_email} onChange={update('contact_email')} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="you@example.com" />
            </div>
          </div>

          <button data-testid="sub-submit" disabled={status === 'submitting'} type="submit" className="w-full bg-teal-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-teal-700 disabled:opacity-60">
            {status === 'submitting' ? 'Submitting…' : 'Submit my truck →'}
          </button>
          <p className="text-xs text-gray-500 text-center">By submitting you confirm you operate this truck or have permission to list it.</p>
        </form>
      </main>
      <Footer />
    </div>
  );
}
